import type { FC, PropsWithChildren, RefObject } from "react";
import { useMemo } from "react";
import ReactDOM from "react-dom";

import { useLocatorTranslations } from "@locators/components/shared/hooks/useLocatorTranslations";
import { useStoreLocatorContext } from "@locators/components/shared/hooks/useStoreLocatorContext";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { useLocale } from "@jti/app-context/useLocale";
import { ChevronForwardIcon } from "@jti/brand-icons/chevron-forward";
import { Button } from "@jti/core-components/components/Button";
import { extractLanguageCode, type FlotiqStore } from "@jti/fe-common";

import { useDirectionURL } from "../../hooks/useDirectionUrl/useDirectionUrl";
import { EventDate } from "../EventDate";
import { StoreLocatorGetDirection } from "../StoreLocatorCard/components/StoreLocatorGetDirection/StoreLocatorGetDirection";
import {
  getCurrentDayShopStatus,
  getDayOfWeekMondayFirst,
  getNextDayOpenTime,
} from "../StoreLocatorCard/components/StoreLocatorShopSchedule/utils";
import { StoreLocatorOpeningStatus } from "../StoreLocatorOpeningStatus/StoreLocatorOpeningStatus";

import { StoreLocatorAvailableProducts } from "./components";

import cardStyles from "./../StoreLocatorCard/StoreLocatorCard.module.scss";
import styles from "./StoreLocatorCardV2.module.scss";

type StoreLocatorCardProps = {
  store: FlotiqStore;
  className?: string;
  isListView?: boolean;
  mapWrapperRef?: RefObject<HTMLDivElement>;
  storeDetailsPageUrl?: string;
  ctaLabel?: string;
  isAddressLabelVisible?: boolean;
  onSelectCard?: (storeId: string, storeName: string, storetype: string) => void;
};

type StoreWithEventDate = FlotiqStore & {
  StartDate?: string;
  EndDate?: string;
};

export const StoreLocatorCardV2: FC<PropsWithChildren<StoreLocatorCardProps>> = ({
  store,
  className,
  isListView,
  mapWrapperRef,
  storeDetailsPageUrl,
  ctaLabel,
  isAddressLabelVisible = true,
  onSelectCard,
}) => {
  const { __ } = useLocatorTranslations();
  const {
    isOpeningHoursEnabled,
    isOpeningHoursBreaksEnabled,
    isAvailableDevicesEnabled,
    isAvailableSticksEnabled,
    storeDetailsPageTarget,
    isStoreCardAddressEnabled,
    translationPrefix,
    onChooseStore,
    isNoMatchingFlotiqStores,
  } = useStoreLocatorContext();
  const isOpeningHoursBlockVisible = isOpeningHoursEnabled && !!store.opening_hours?.length;
  const isAvailableProductsVisible =
    !isStoreCardAddressEnabled &&
    (isAvailableDevicesEnabled || isAvailableSticksEnabled) &&
    !!store.product_attributes?.length;
  const locale = useLocale();
  const lng = extractLanguageCode(locale);
  const { locationServiceMapProvider } = useConfig();
  const directionURL = useDirectionURL({ store, locationServiceMapProvider });

  const openingHours = store.opening_hours?.[0];
  const days = openingHours?.days;
  const time_zone = openingHours?.time_zone;

  const dayOfWeekMondayFirst = getDayOfWeekMondayFirst();
  const currentDayShopSchedule = days?.[dayOfWeekMondayFirst];
  const nextDayOpenTime = useMemo(() => (days?.length ? getNextDayOpenTime(days) : undefined), [days]);
  const hasStoreDetailsPageUrl = Boolean(storeDetailsPageUrl) && !onChooseStore;
  const isEventCard = translationPrefix === "events";
  const eventStore = store as StoreWithEventDate;
  const { shopOpeningStatus, shopOpeningHours } = useMemo(
    () =>
      nextDayOpenTime && currentDayShopSchedule
        ? getCurrentDayShopStatus(
            currentDayShopSchedule,
            time_zone ?? "",
            isOpeningHoursBreaksEnabled ?? false,
            isListView ?? false,
            nextDayOpenTime ?? ""
          )
        : { shopOpeningHours: undefined, shopOpeningStatus: undefined },
    [currentDayShopSchedule, time_zone, isOpeningHoursBreaksEnabled, isListView, nextDayOpenTime]
  );

  const hasCtaLink = !!store?.CtaHref;
  const isDirectionsCardFallbackEnabled =
    !hasCtaLink &&
    !hasStoreDetailsPageUrl &&
    Boolean(directionURL) &&
    (translationPrefix === "events" || translationPrefix === "horeca");

  const handleOnClick = () => {
    onSelectCard?.(store.id, store.StoreName, store.storetype ?? "");

    if (hasCtaLink) {
      return;
    }

    if (hasStoreDetailsPageUrl) {
      const currentUrl = window.location.origin;

      const detailsPageUrl = storeDetailsPageUrl ?? "";
      const pathName = detailsPageUrl.startsWith(`/${lng}`) ? `${detailsPageUrl}` : `/${lng}/${detailsPageUrl}`;

      const newUrl = `${currentUrl}${pathName}`.replace(/([^:]\/)\/+/g, "$1");

      if (storeDetailsPageTarget === "_blank") {
        window.open(newUrl, "_blank");
      } else {
        window.location.replace(newUrl);
      }

      return;
    }

    if (isDirectionsCardFallbackEnabled) {
      window.open(directionURL, "_blank", "noopener,noreferrer");
    }
  };

  if (!store) {
    return null;
  }

  const CardContent = (
    <article
      data-testid="storeLocatorCardV2"
      {...(!hasCtaLink && { onClick: handleOnClick })}
      rel="noreferrer"
      className={classNames(cardStyles.resultCard, styles.resulCardV2, className, {
        [cardStyles.resultCardActive]: !isListView,
        [styles.horecaCard]: translationPrefix === "horeca",
      })}
    >
      <div className={classNames(cardStyles.cardHeader, styles.cardHeader)}>
        <p className={classNames(cardStyles.resultCardShopName, styles.resultCardShopName)}>{store.StoreName}</p>
        {hasCtaLink || isDirectionsCardFallbackEnabled ? (
          <StoreLocatorGetDirection
            isLinkLabel={false}
            href={directionURL}
            className={classNames(styles.storeCardChevron, styles.getLocation)}
            onClick={isDirectionsCardFallbackEnabled ? (event) => event.stopPropagation() : undefined}
          />
        ) : hasStoreDetailsPageUrl || translationPrefix !== "horeca" ? (
          <ChevronForwardIcon className={styles.storeCardChevron} />
        ) : null}
      </div>
      {(isOpeningHoursBlockVisible || isAvailableProductsVisible) && (
        <div
          className={classNames(cardStyles.cardInfoWrapper, styles.cardInfoWrapper, {
            [cardStyles.cardInfoSingleView]: !isListView && !isOpeningHoursBlockVisible,
          })}
        >
          {isOpeningHoursBlockVisible && shopOpeningStatus && shopOpeningHours && (
            <StoreLocatorOpeningStatus
              shopOpeningStatus={shopOpeningStatus}
              shopOpeningHours={shopOpeningHours}
              isNewStoreCardEnabled
            />
          )}
          {isAvailableProductsVisible && (
            <StoreLocatorAvailableProducts
              productAttributes={store.product_attributes ?? []}
              isListView={isListView ?? false}
            />
          )}
        </div>
      )}
      {isStoreCardAddressEnabled && (
        <address className={styles.storeCardAddressItem}>
          {isAddressLabelVisible && !isEventCard && (
            <span className={styles.storeCardAddressLabel}>{__("store_locator.popup.address_label")}</span>
          )}
          {store.address}
        </address>
      )}
      {isEventCard && <EventDate startDate={eventStore.StartDate} endDate={eventStore.EndDate} />}
      {onChooseStore && !hasCtaLink && (
        <div className={classNames(cardStyles.cardFooter, styles.cardFooter, styles.selectStoreFooter)}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onChooseStore(store)}
            disabled={isNoMatchingFlotiqStores}
            data-testid="storeLocatorCardV2SelectStore"
          >
            {__("storelocator.select_store")}
          </Button>
        </div>
      )}
      {hasCtaLink && !onChooseStore && (
        <div className={classNames(cardStyles.cardFooter, styles.cardFooter)}>
          <Button
            variant="secondary"
            size="sm"
            fullWidth
            href={storeDetailsPageUrl ?? ""}
            target={storeDetailsPageTarget}
            rel="noreferrer"
            data-testid="getDetailsPageLink"
          >
            {ctaLabel || __("store_locator.popup.store_details")}
          </Button>
        </div>
      )}
    </article>
  );

  if (mapWrapperRef?.current && !isListView) {
    return ReactDOM.createPortal(
      <div className={styles.cardLocatorMapWrapper}>{CardContent}</div>,
      mapWrapperRef.current
    );
  }

  return CardContent;
};
